import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import debounce from 'lodash.debounce';
import { createWatchlist, downloadExportCsv } from '../services/api';

// Investment-thesis tag vocabulary (#771 / INIT-thesis-tagger).
// Mirrors THESIS_TAG_VOCAB in backend/src/landai/api/v1/parcels.py and
// THESIS_TAGS in scripts/compute_investment_thesis_tags.py — keep in
// sync if a tag is added or renamed. Labels are short for the chip
// strip; the parcel-detail card uses the same labels.
const THESIS_FILTER_TAGS = [
  { tag: 'off-grid-homestead', label: 'Off-grid', tip: 'Large vacant rural · renewables · low transmission' },
  { tag: 'commercial-timber', label: 'Timber', tip: 'Forested · timber belt · slope-friendly' },
  { tag: 'solar-lease-prospect', label: 'Solar lease', tip: 'Flat · high solar score · near transmission' },
  { tag: 'mineral-exploration', label: 'Mineral', tip: 'High commodity probability · low building value' },
  { tag: 'hunting-recreation', label: 'Hunting', tip: 'Large · forested or mixed habitat · low buildings' },
  { tag: 'agri-cash-rent', label: 'Cash-rent ag', tip: 'Flat tillable · farm belt · ≥40ac' },
];

function ThesisFilter({ filters, onChange }) {
  const [counts, setCounts] = React.useState({});
  const stateScope = filters.state || '';
  React.useEffect(() => {
    let cancelled = false;
    const url = stateScope
      ? `/api/v1/parcels/thesis_tags/counts?state=${encodeURIComponent(stateScope)}`
      : `/api/v1/parcels/thesis_tags/counts`;
    fetch(url)
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (cancelled || !d || !Array.isArray(d.counts)) return;
        const map = {};
        for (const r of d.counts) map[r.tag] = r.count;
        setCounts(map);
      })
      .catch(() => { /* ignore — chip strip still renders without counts */ });
    return () => { cancelled = true; };
  }, [stateScope]);

  const selected = filters.thesisTag || '';
  const handleClick = (tag) => {
    // Toggle: clicking the active chip clears the filter.
    if (selected === tag) {
      onChange('');
    } else {
      onChange(tag);
    }
  };

  return (
    <div className="filter-group">
      <label>Investment thesis</label>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {THESIS_FILTER_TAGS.map(({ tag, label, tip }) => {
          const n = counts[tag];
          const isSelected = selected === tag;
          const empty = n === 0;
          return (
            <button
              key={tag}
              type="button"
              disabled={empty && !isSelected}
              onClick={() => handleClick(tag)}
              title={`${tip}${n != null ? ` · ${n.toLocaleString()} parcels${stateScope ? ` in ${stateScope}` : ''}` : ''}`}
              style={{
                padding: '4px 10px',
                borderRadius: 999,
                border: '1px solid',
                borderColor: isSelected ? '#1e3a8a' : '#cbd5e1',
                background: isSelected ? '#1e3a8a' : '#f8fafc',
                color: isSelected ? '#fff' : (empty ? '#9ca3af' : '#1f2937'),
                fontSize: '0.78rem',
                cursor: empty && !isSelected ? 'not-allowed' : 'pointer',
                opacity: empty && !isSelected ? 0.55 : 1,
              }}
            >
              {label}
              {n != null && (
                <span style={{
                  marginLeft: 6, fontSize: '0.68rem',
                  opacity: 0.8, fontFamily: 'ui-monospace, monospace',
                }}>
                  {n >= 1000 ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k` : n}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}


function FilterPanel({ filters, setFilters, group, actionsOnly = false }) {
  const [error, setError] = useState(null);
  const [savingWatchlist, setSavingWatchlist] = useState(false);
  const navigate = useNavigate();

  // NOTE: Filter keys in the UI state are camelCase (minArea, maxArea,
  // isListed, minPrice, maxPrice) — see MapView's ViewportLoader for
  // the canonical list. Earlier versions of this component mixed in
  // snake_case reads (filters.min_acres, filters.is_listed) that were
  // always undefined, so Save-as-Watchlist and Export CSV silently
  // dropped half the active filters.
  const handleSaveAsWatchlist = async () => {
    const hasFilters = filters.state || filters.minArea || filters.maxArea ||
      filters.minPrice || filters.maxPrice || filters.isListed;
    if (!hasFilters) return;

    const name = prompt('Name this watchlist:', `${filters.state || 'All'} ${filters.isListed ? 'listed' : ''} land`.trim());
    if (!name) return;

    setSavingWatchlist(true);
    try {
      const payload = { name, listed_only: !!filters.isListed };
      if (filters.state) payload.states = [filters.state];
      if (filters.minArea) payload.min_acres = parseFloat(filters.minArea);
      if (filters.maxArea) payload.max_acres = parseFloat(filters.maxArea);
      if (filters.maxPrice) payload.max_price = parseFloat(filters.maxPrice);
      await createWatchlist(payload);
      navigate('/watchlists');
    } catch {
      alert('Failed to create watchlist');
    } finally {
      setSavingWatchlist(false);
    }
  };

  // Export CSV (#1142). The /export endpoint is auth-gated (#280), so the
  // legacy `window.open('/api/v1/export?...')` navigation failed with
  // "Not Authenticated" — a bare browser request cannot carry the Bearer
  // token. Pull the bytes through the authenticated apiClient as a Blob
  // and trigger a client-side download, mirroring Properties.handleExportCsv.
  const handleExportCsv = async () => {
    const params = {};
    if (filters.state) params.state = filters.state;
    if (filters.minArea) params.min_area = filters.minArea;
    if (filters.maxArea) params.max_area = filters.maxArea;
    if (filters.isListed) params.is_listed = 'true';
    if (filters.offMarketOnly) params.is_listed = 'false';
    if (filters.q) params.q = filters.q;
    params.limit = '10000';
    try {
      const blob = await downloadExportCsv(params);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'parcels.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      alert('Failed to export CSV');
    }
  };

  // All 50 US states + territories, plus international pilot entries
  // (Uruguay/Maldonado — #1255 M4).
  const states = [
    { value: 'AL', name: 'Alabama' }, { value: 'AK', name: 'Alaska' },
    { value: 'AZ', name: 'Arizona' }, { value: 'AR', name: 'Arkansas' },
    { value: 'CA', name: 'California' }, { value: 'CO', name: 'Colorado' },
    { value: 'CT', name: 'Connecticut' }, { value: 'DE', name: 'Delaware' },
    { value: 'FL', name: 'Florida' }, { value: 'GA', name: 'Georgia' },
    { value: 'HI', name: 'Hawaii' }, { value: 'ID', name: 'Idaho' },
    { value: 'IL', name: 'Illinois' }, { value: 'IN', name: 'Indiana' },
    { value: 'IA', name: 'Iowa' }, { value: 'KS', name: 'Kansas' },
    { value: 'KY', name: 'Kentucky' }, { value: 'LA', name: 'Louisiana' },
    { value: 'ME', name: 'Maine' }, { value: 'MD', name: 'Maryland' },
    { value: 'MA', name: 'Massachusetts' }, { value: 'MI', name: 'Michigan' },
    { value: 'MN', name: 'Minnesota' }, { value: 'MS', name: 'Mississippi' },
    { value: 'MO', name: 'Missouri' }, { value: 'MT', name: 'Montana' },
    { value: 'NE', name: 'Nebraska' }, { value: 'NV', name: 'Nevada' },
    { value: 'NH', name: 'New Hampshire' }, { value: 'NJ', name: 'New Jersey' },
    { value: 'NM', name: 'New Mexico' }, { value: 'NY', name: 'New York' },
    { value: 'NC', name: 'North Carolina' }, { value: 'ND', name: 'North Dakota' },
    { value: 'OH', name: 'Ohio' }, { value: 'OK', name: 'Oklahoma' },
    { value: 'OR', name: 'Oregon' }, { value: 'PA', name: 'Pennsylvania' },
    { value: 'RI', name: 'Rhode Island' }, { value: 'SC', name: 'South Carolina' },
    { value: 'SD', name: 'South Dakota' }, { value: 'TN', name: 'Tennessee' },
    { value: 'TX', name: 'Texas' }, { value: 'UT', name: 'Utah' },
    { value: 'UY', name: 'Uruguay' },
    { value: 'VT', name: 'Vermont' }, { value: 'VA', name: 'Virginia' },
    { value: 'WA', name: 'Washington' }, { value: 'WV', name: 'West Virginia' },
    { value: 'WI', name: 'Wisconsin' }, { value: 'WY', name: 'Wyoming' },
  ];

  const validateInputs = useCallback((minArea, maxArea) => {
    const min = parseFloat(minArea);
    const max = parseFloat(maxArea);
    if (!isNaN(min) && !isNaN(max) && min > max) {
      return 'Min area must be less than max area';
    }
    return null;
  }, []);

  const debouncedValidate = useCallback(
    debounce((minArea, maxArea) => {
      setError(validateInputs(minArea, maxArea));
    }, 300),
    [validateInputs]
  );

  useEffect(() => {
    debouncedValidate(filters.minArea, filters.maxArea);
  }, [filters.minArea, filters.maxArea, debouncedValidate]);

  const handleChange = (name, value) => {
    setFilters(prev => {
      const next = { ...prev, [name]: value };
      // #997: clearing the generation distance clears any tech restriction
      // too — a `generation_tech` without a distance cap is meaningless
      // (the backend ignores it) and would leave a dangling filter pill.
      if (name === 'maxGenerationDist' && !value) next.generationTech = '';
      return next;
    });
  };

  // #704 follow-up — FilterPanel renders grouped. When `group` is one of
  // the accordion / sheet section keys, only that section's controls
  // render, so FilterPanelAccordion can place real inputs INSIDE each
  // accordion section instead of describing them and dumping the whole
  // flat panel below. No `group` → render every control (the legacy flat
  // layout, still used by FilterPanel.test.js and any direct fallback).
  const showGroup = (g) => !group || group === g;

  // Action buttons (Apply / Reset / Export / Save) belong at the bottom
  // ONCE, not repeated per group. In grouped mode the wrapper renders a
  // single <FilterPanel actionsOnly /> beneath the sections to host them.
  const actions = (
    <>
      <button
        className="btn btn-primary apply-filters"
        onClick={() => setFilters(prev => ({ ...prev }))}
        disabled={!!error}
        style={{ width: '100%' }}
      >
        Apply Filters
      </button>

      {/* Reset Filters (#113) — clears all current filter values back to
          the default empty state. Implemented by replacing filters with
          `{}` since SearchPage's `useState({})` and downstream consumers
          (MapView/ViewportLoader/Export CSV) treat missing keys as "no
          constraint". */}
      <button
        type="button"
        className="btn btn-outline reset-filters"
        onClick={() => setFilters({})}
        style={{ width: '100%', marginTop: '0.5rem', fontSize: '0.8rem' }}
        title="Clear all filters back to defaults"
      >
        Reset Filters
      </button>

      <button
        className="btn btn-outline"
        onClick={handleExportCsv}
        style={{ width: '100%', marginTop: '0.5rem', fontSize: '0.8rem' }}
      >
        Export CSV
      </button>

      <button
        className="btn btn-outline"
        onClick={handleSaveAsWatchlist}
        disabled={savingWatchlist || !(filters.state || filters.minArea || filters.maxArea || filters.minPrice || filters.maxPrice || filters.isListed)}
        style={{ width: '100%', marginTop: '0.5rem', fontSize: '0.8rem' }}
        title="Save current filters as a watchlist to get alerts when new matches appear"
      >
        {savingWatchlist ? 'Saving...' : 'Save as Watchlist'}
      </button>
    </>
  );

  // Grouped-mode footer: the wrapper renders just the action buttons.
  if (actionsOnly) {
    return <div className="filter-panel filter-panel--actions">{actions}</div>;
  }

  return (
    <div className="filter-panel">
      {!group && <h2>Filters</h2>}

      {showGroup('location-size') && (
      <>
      {/* Text Search */}
      <div className="filter-group">
        <label>Search</label>
        <input
          type="text"
          placeholder="Search by APN, address, owner, or MLS#..."
          value={filters.q || ''}
          onChange={(e) => handleChange('q', e.target.value)}
        />
      </div>

      {/* Size Filter */}
      <div className="filter-group">
        <label>Area (acres)</label>
        {error && <div className="validation-error" role="alert">{error}</div>}
        <div className="range-inputs">
          <input type="number" placeholder="Min" value={filters.minArea || ''} onChange={(e) => handleChange('minArea', e.target.value)} />
          <input type="number" placeholder="Max" value={filters.maxArea || ''} onChange={(e) => handleChange('maxArea', e.target.value)} />
        </div>
      </div>

      {/* Location Filter */}
      <div className="filter-group">
        <label>State</label>
        <select value={filters.state || ''} onChange={(e) => handleChange('state', e.target.value)}>
          <option value="">All States</option>
          {states.map((state) => (
            <option key={state.value} value={state.value}>{state.name}</option>
          ))}
        </select>
      </div>
      </>
      )}

      {showGroup('land-quality') && (
      <>
      {/* Listing Status — tri-state radio. Off-market filtering
          (#99) lets users prospect parcels that aren't on MLS. */}
      <div className="filter-group">
        <label>Listing Status</label>
        <div className="radio-group" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label className="radio-label" style={{ whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6 }}>
            <input
              type="radio"
              name="listing-status"
              checked={!filters.isListed && !filters.offMarketOnly}
              onChange={() => {
                handleChange('isListed', false);
                handleChange('offMarketOnly', false);
              }}
            />
            <span style={{ whiteSpace: 'nowrap' }}>All parcels</span>
          </label>
          <label className="radio-label" style={{ whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6 }}>
            <input
              type="radio"
              name="listing-status"
              checked={!!filters.isListed}
              onChange={() => {
                handleChange('isListed', true);
                handleChange('offMarketOnly', false);
              }}
            />
            <span style={{ whiteSpace: 'nowrap' }}>Active MLS listings only</span>
          </label>
          <label className="radio-label" style={{ whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6 }}>
            <input
              type="radio"
              name="listing-status"
              checked={!!filters.offMarketOnly}
              onChange={() => {
                handleChange('isListed', false);
                handleChange('offMarketOnly', true);
              }}
              title="Off-market parcels only — for prospecting unsold land"
            />
            <span style={{ whiteSpace: 'nowrap' }}>Off-market only</span>
          </label>
        </div>
      </div>

      {/* Bookmarks Filter */}
      <div className="filter-group">
        <label>Saved Items</label>
        <div className="checkbox-group">
          <label className="checkbox-label">
            <input type="checkbox" checked={filters.bookmarksOnly || false} onChange={(e) => handleChange('bookmarksOnly', e.target.checked)} />
            <span>My bookmarks only</span>
          </label>
          {/* Hidden / downvoted parcels (#124). Default OFF — hidden
              parcels disappear from the map and lists entirely.
              When ON, they reappear tinted red (#dc2626) so the
              user can review or un-hide them. */}
          <label className="checkbox-label" title="Show parcels you previously hid (rendered in red on the map)">
            <input
              type="checkbox"
              checked={filters.showHidden || false}
              onChange={(e) => handleChange('showHidden', e.target.checked)}
            />
            <span>Show hidden</span>
          </label>
          {/* Not-viewed filter (#183) — hides parcels you've already
              opened in the list panel. Client-side so no round-trip. */}
          <label className="checkbox-label" title="Hide parcels you've already opened (list view only)">
            <input
              type="checkbox"
              checked={filters.notViewed || false}
              onChange={(e) => handleChange('notViewed', e.target.checked)}
            />
            <span>Not viewed only</span>
          </label>
          {/* Color-by-owner toggle (#703 follow-up). When ON, visible
              parcel boundaries on the map are color-coded by owner via
              the same ownerColor() hash the parcel-detail overlay uses
              — so same-owner clusters jump out visually. Default OFF
              because the standard listed/unlisted palette is what most
              users expect. */}
          <label className="checkbox-label" title="Color parcel polygons by owner — same color = same owner">
            <input
              type="checkbox"
              checked={filters.colorByOwner || false}
              onChange={(e) => handleChange('colorByOwner', e.target.checked)}
            />
            <span>Color by owner</span>
          </label>
        </div>
      </div>

      {/* Encumbrance filter (#610). Most buyers want to skip
          encumbered parcels by default; distress-hunting investors
          have a separate screen (PR-C). Backend filters has_lien =
          TRUE / has_easement = TRUE out when these are checked.
          NULL ("no data") still passes — we don't want to hide
          parcels just because we haven't yet confirmed absence. */}
      <div className="filter-group">
        <label>Encumbrances</label>
        <div className="checkbox-group">
          <label
            className="checkbox-label"
            title="Hide parcels with a recorded lien"
          >
            <input
              type="checkbox"
              checked={!!filters.excludeLiens}
              onChange={(e) => handleChange('excludeLiens', e.target.checked)}
            />
            <span>Hide liens</span>
          </label>
          <label
            className="checkbox-label"
            title="Hide parcels with a recorded easement"
          >
            <input
              type="checkbox"
              checked={!!filters.excludeEasements}
              onChange={(e) => handleChange('excludeEasements', e.target.checked)}
            />
            <span>Hide easements</span>
          </label>
          {/* Landlocked filter (#641 C1). Hide parcels whose nearest
              public road is > 402m (¼ mile) per
              scripts/compute_road_access.py. NULL passes — parcels
              without road-access data (compute hasn't run for that
              state yet) are not hidden. */}
          <label
            className="checkbox-label"
            title="Hide parcels with no public road within ¼ mile"
          >
            <input
              type="checkbox"
              checked={!!filters.excludeLandlocked}
              onChange={(e) => handleChange('excludeLandlocked', e.target.checked)}
            />
            <span>Hide landlocked</span>
          </label>
        </div>
      </div>

      {/* Motivated-seller filter (#629). Surfaces only parcels with a
          Realie-recorded foreclosure / auction / lien-pending status.
          The complement of "Hide liens" — this is the distress-hunting
          screen for investors, not the conservative-buyer default. */}
      <div className="filter-group">
        <label>Motivated Sellers</label>
        <div className="checkbox-group">
          <label
            className="checkbox-label"
            title="Only parcels with a foreclosure or auction recorded"
          >
            <input
              type="checkbox"
              checked={!!filters.distressedOnly}
              onChange={(e) => handleChange('distressedOnly', e.target.checked)}
            />
            <span>Distressed only</span>
          </label>
          {/* Land-only confirmed (#630). When Realie reports
              total_building_value ≈ 0 the parcel is provably
              land-only; this is the corroborating signal to the
              land_use_code filter. */}
          <label
            className="checkbox-label"
            title="Only parcels Realie reports as having no building value"
          >
            <input
              type="checkbox"
              checked={!!filters.landOnlyConfirmed}
              onChange={(e) => handleChange('landOnlyConfirmed', e.target.checked)}
            />
            <span>Land-only confirmed</span>
          </label>
        </div>
      </div>

      {/* Quick-screen preset (#630). Single-click setup of the
          opportunity screen: stale-DOM + price-cut + distressed +
          land-only confirmed. ANDing these four signals surfaces
          the highest-conviction motivated-seller / land-only
          opportunities in one query. The preset only toggles the
          four screen-specific filters — the user's other selections
          (state, acreage, price band, etc.) stay as-is so the screen
          composes naturally with geographic scoping. */}
      <div className="filter-group">
        <label>Quick Screens</label>
        <div className="checkbox-group">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              const enabled = !!filters.priceChangedRecently
                && !!filters.distressedOnly
                && !!filters.landOnlyConfirmed
                && (filters.minDom === 60 || filters.minDom === '60');
              if (enabled) {
                handleChange('priceChangedRecently', false);
                handleChange('distressedOnly', false);
                handleChange('landOnlyConfirmed', false);
                handleChange('minDom', '');
              } else {
                handleChange('priceChangedRecently', true);
                handleChange('priceChangeDays', '60');
                handleChange('distressedOnly', true);
                handleChange('landOnlyConfirmed', true);
                handleChange('minDom', '60');
              }
            }}
            style={{
              padding: '6px 10px', fontSize: '0.78rem', width: '100%',
              textAlign: 'left',
              background: (!!filters.priceChangedRecently
                && !!filters.distressedOnly
                && !!filters.landOnlyConfirmed
                && (filters.minDom === 60 || filters.minDom === '60'))
                ? '#1e3a8a' : undefined,
            }}
            title="Sets: 60+ days on market AND a price cut within 60d AND distressed AND land-only confirmed"
          >
            Motivated seller · land-only
          </button>
        </div>
      </div>

      {/* Investment-thesis tag chip strip (#771 / INIT-thesis-tagger).
          Single-select for MVP — toggling a chip swaps the filter or
          clears it. Counts come from /parcels/thesis_tags/counts and
          scope to the selected state when one is set. Chips with zero
          matches still render so users can see the full vocabulary; we
          gray them out and disable selection on those. */}
      <ThesisFilter
        filters={filters}
        onChange={(tag) => handleChange('thesisTag', tag)}
      />

      {/* Max climate risk slider (#773 / INIT-climate-risk).
          Filters to parcels whose `climate_risk_total_0_100` (the linear
          weighted FEMA NRI composite written by
          scripts/compute_climate_risk.py) is at or below the chosen value.
          Default of 100 (no filter) renders as "Any" — only emits the
          query param when the user moves the slider down.

          NULL-score parcels (no FEMA NRI tract match yet) are excluded
          on the backend whenever this filter is set — see /parcels
          handler in backend/src/landai/api/v1/parcels.py. We expose
          that behaviour as a help-text caveat rather than a separate
          checkbox to keep the slider simple. */}
      <div className="filter-group">
        <label>
          Max Climate Risk
          <span className="score-value">
            {filters.maxClimateRisk != null && filters.maxClimateRisk !== ''
              ? `≤ ${filters.maxClimateRisk}`
              : 'Any'}
          </span>
        </label>
        <input
          type="range"
          min="0"
          max="100"
          step="1"
          value={filters.maxClimateRisk != null && filters.maxClimateRisk !== ''
            ? filters.maxClimateRisk
            : 100}
          onChange={(e) => {
            const v = parseInt(e.target.value, 10);
            // 100 == no filter; clear so the URL doesn't carry a
            // pointless `?max_climate_risk=100`.
            handleChange('maxClimateRisk', v >= 100 ? '' : v);
          }}
          style={{ width: '100%' }}
          aria-label="Maximum climate-risk score"
        />
        <div style={{ display: 'flex', justifyContent: 'space-between',
                      fontSize: '0.7rem', color: '#888' }}>
          <span>0 (low)</span>
          <span>50</span>
          <span>100 (high)</span>
        </div>
        <p style={{ fontSize: '0.7rem', color: '#999', margin: '4px 0 0 0' }}>
          Composite FEMA NRI score. Parcels without NRI tract data are
          excluded when this filter is active.
        </p>
      </div>

      {/* Min listing quality slider (#775 / INIT-listing-quality).
          Filters to active listings whose
          `listing_quality_score_0_100` (per-LISTING composite computed
          by scripts/compute_listing_quality.py) is at or above the
          chosen value. Default of 0 renders as "Any" — only emits the
          query param when the user moves the slider up.

          The backend index is partial on `listing_quality_score_0_100
          IS NOT NULL`, so this filter is also implicitly an "only
          parcels with an active scored listing" filter — surfaced as a
          help-text caveat. */}
      <div className="filter-group">
        <label>
          Min Listing Quality
          <span className="score-value">
            {filters.minListingQuality != null && filters.minListingQuality !== ''
              ? `≥ ${filters.minListingQuality}`
              : 'Any'}
          </span>
        </label>
        <input
          type="range"
          min="0"
          max="100"
          step="1"
          value={filters.minListingQuality != null && filters.minListingQuality !== ''
            ? filters.minListingQuality
            : 0}
          onChange={(e) => {
            const v = parseInt(e.target.value, 10);
            // 0 == no filter; clear so the URL doesn't carry a
            // pointless `?min_listing_quality=0`.
            handleChange('minListingQuality', v <= 0 ? '' : v);
          }}
          style={{ width: '100%' }}
          aria-label="Minimum listing-quality score"
        />
        <div style={{ display: 'flex', justifyContent: 'space-between',
                      fontSize: '0.7rem', color: '#888' }}>
          <span>0 (any)</span>
          <span>60 (high)</span>
          <span>100 (top)</span>
        </div>
        <p style={{ fontSize: '0.7rem', color: '#999', margin: '4px 0 0 0' }}>
          Composite score over photos, description, price, freshness.
          Parcels without an active scored listing are excluded when
          this filter is active.
        </p>
      </div>

      {/* Min 60-day sale probability slider (#777 / INIT-sale-probability).
          Filters to active listings whose `sale_probability_60d`
          (per-LISTING heuristic computed by
          scripts/compute_sale_probability.py) is at or above the
          chosen value. UI grain is an integer percent (0-100);
          SearchPage downconverts to a 0-1 float before passing to the
          API. Default of 0 renders as "Any".

          The backend index is partial on `sale_probability_60d IS NOT
          NULL`, so this filter implicitly excludes listings that haven't
          been scored yet. */}
      <div className="filter-group">
        <label>
          Min 60d Sale Probability
          <span className="score-value">
            {filters.minSaleProbability60d != null && filters.minSaleProbability60d !== ''
              ? `≥ ${filters.minSaleProbability60d}%`
              : 'Any'}
          </span>
        </label>
        <input
          type="range"
          min="0"
          max="100"
          step="1"
          value={filters.minSaleProbability60d != null && filters.minSaleProbability60d !== ''
            ? filters.minSaleProbability60d
            : 0}
          onChange={(e) => {
            const v = parseInt(e.target.value, 10);
            // 0 == no filter; clear so the URL doesn't carry a
            // pointless `?min_sale_probability_60d=0`.
            handleChange('minSaleProbability60d', v <= 0 ? '' : v);
          }}
          style={{ width: '100%' }}
          aria-label="Minimum 60-day sale probability"
        />
        <div style={{ display: 'flex', justifyContent: 'space-between',
                      fontSize: '0.7rem', color: '#888' }}>
          <span>0% (any)</span>
          <span>40% (likely)</span>
          <span>100%</span>
        </div>
        <p style={{ fontSize: '0.7rem', color: '#999', margin: '4px 0 0 0' }}>
          Heuristic from DOM, listing quality, price-vs-comparable, and
          aesthetics. Listings without a scored probability are excluded
          when this filter is active.
        </p>
      </div>

      {/* Max % above fair value slider (#779 / INIT-fair-value).
          Filters to listings whose `listing_price` is at or below
          `fair_value_upper_80 * (1 + N/100)` — i.e. "show me listings
          no more than N% above the model's 80% CI upper bound".

          UI grain is a percent in [-50, 500]; backend takes the raw
          number (no 0-1 conversion). Default of 'Any' (null) renders
          as no filter; setting the slider to 0 keeps only listings at
          or below the upper-80 CI ("not overpriced vs model").

          IMPORTANT CAVEAT (also echoed in the ParcelDetail mini-card):
          the model predicts LISTED prices, not sold prices. The
          backend index is partial on `fair_value_estimate IS NOT NULL`,
          so this filter implicitly excludes listings that haven't been
          scored yet. */}
      <div className="filter-group">
        <label>
          Max % Above Fair Value
          <span className="score-value">
            {filters.maxPctAboveFairValue != null && filters.maxPctAboveFairValue !== ''
              ? `≤ ${filters.maxPctAboveFairValue}%`
              : 'Any'}
          </span>
        </label>
        <input
          type="range"
          min="-50"
          max="200"
          step="5"
          value={filters.maxPctAboveFairValue != null && filters.maxPctAboveFairValue !== ''
            ? filters.maxPctAboveFairValue
            : 200}
          onChange={(e) => {
            const v = parseInt(e.target.value, 10);
            // 200 = the slider's right end, treated as "no filter" so
            // the URL doesn't carry a pointless `?max_pct_above_fair_value=200`.
            // (Listings can still exceed 200% — they just won't be
            // filtered by this slider.)
            handleChange('maxPctAboveFairValue', v >= 200 ? '' : v);
          }}
          style={{ width: '100%' }}
          aria-label="Maximum percent above fair value"
        />
        <div style={{ display: 'flex', justifyContent: 'space-between',
                      fontSize: '0.7rem', color: '#888' }}>
          <span>-50% (deep under)</span>
          <span>0% (in CI)</span>
          <span>200% (any)</span>
        </div>
        <p style={{ fontSize: '0.7rem', color: '#999', margin: '4px 0 0 0' }}>
          Caveat: model is trained on LISTED prices (not sold prices). Use
          as an outlier detector, not a market valuation. Listings without
          a scored fair-value estimate are excluded when this filter is set.
        </p>
      </div>

      {/* Min expected 5y yield slider (#785 / INIT-yield-calc).
          Filters to active listings whose `expected_5y_yield_pct`
          (per-LISTING rule-based synthesis computed by
          scripts/compute_investment_yield.py) is at or above the
          chosen value. UI grain is an integer percent in [0, 30] —
          the upper bound matches the CLI cap from the formula
          docstring; 0 renders as "Any" and the slider clears the
          URL key.

          The backend index is partial on `expected_5y_yield_pct IS
          NOT NULL`, so this filter implicitly excludes listings that
          haven't been scored yet. */}
      <div className="filter-group">
        <label>
          Min Expected 5y Yield
          <span className="score-value">
            {filters.minExpectedYield != null && filters.minExpectedYield !== ''
              ? `≥ ${filters.minExpectedYield}%`
              : 'Any'}
          </span>
        </label>
        <input
          type="range"
          min="0"
          max="30"
          step="1"
          value={filters.minExpectedYield != null && filters.minExpectedYield !== ''
            ? filters.minExpectedYield
            : 0}
          onChange={(e) => {
            const v = parseInt(e.target.value, 10);
            // 0 == no filter; clear so the URL doesn't carry a
            // pointless `?min_expected_yield=0`.
            handleChange('minExpectedYield', v <= 0 ? '' : v);
          }}
          style={{ width: '100%' }}
          aria-label="Minimum expected 5-year annualised yield"
        />
        <div style={{ display: 'flex', justifyContent: 'space-between',
                      fontSize: '0.7rem', color: '#888' }}>
          <span>0% (any)</span>
          <span>10% (strong)</span>
          <span>30% (top)</span>
        </div>
        <p style={{ fontSize: '0.7rem', color: '#999', margin: '4px 0 0 0' }}>
          Rule-based: combines state appreciation, fair-value gap,
          climate-risk discount, and revenue scenarios (solar/wind/timber
          /ag/hunting). Listings without a scored yield are excluded
          when this filter is active.
        </p>
      </div>

      {/* Description-NER filters (#781 / INIT-description-ner). Three
          tri-state booleans extracted from the seller's free-text
          listing description by scripts/extract_description_features.py.
          NULL semantics: an unscored or absence-of-mention listing is
          excluded from positive matches — these filters say "only
          listings that EXPLICITLY say X", not "listings where X is
          known to be true OR unknown". This is intentional: a
          description that doesn't mention a gate isn't proof the
          property isn't gated, but the buyer wants the explicit
          signal. */}
      <div className="filter-group">
        <label>Listing Description Signals</label>
        <div className="checkbox-group">
          <label
            className="checkbox-label"
            title="Only listings whose description explicitly says 'gated community'"
          >
            <input
              type="checkbox"
              checked={!!filters.gatedCommunityOnly}
              onChange={(e) => handleChange('gatedCommunityOnly', e.target.checked)}
            />
            <span>Gated community only</span>
          </label>
          <label
            className="checkbox-label"
            title="Only listings whose description explicitly says 'no HOA'"
          >
            <input
              type="checkbox"
              checked={!!filters.noHoaOnly}
              onChange={(e) => handleChange('noHoaOnly', e.target.checked)}
            />
            <span>No HOA only</span>
          </label>
          <label
            className="checkbox-label"
            title="Only listings whose description explicitly says the parcel is subdividable"
          >
            <input
              type="checkbox"
              checked={!!filters.subdividableOnly}
              onChange={(e) => handleChange('subdividableOnly', e.target.checked)}
            />
            <span>Subdividable only</span>
          </label>
        </div>
        <p style={{ fontSize: '0.7rem', color: '#999', margin: '4px 0 0 0' }}>
          Extracted from free-text descriptions by Claude. Listings
          without an extraction (or no explicit mention) are excluded
          when any of these toggles is on.
        </p>
      </div>

      {/* Photo-content classifier (#783 / INIT-photo-content). Three
          tri-state booleans derived from per-photo Claude-vision tags
          (`shows_house` / `shows_water` / `shows_long_view`) aggregated
          to the listing level. NULL semantics match #781: a listing that
          hasn't been classified isn't proof of absence, so the filter is
          "only show listings whose gallery EXPLICITLY shows X". Listings
          without a classification pass are excluded when any of the
          toggles is on. */}
      <div className="filter-group">
        <label>Listing Photo Signals</label>
        <div className="checkbox-group">
          <label
            className="checkbox-label"
            title="Only listings whose gallery includes a photo of a residential structure"
          >
            <input
              type="checkbox"
              checked={!!filters.hasHousePhotoOnly}
              onChange={(e) => handleChange('hasHousePhotoOnly', e.target.checked)}
            />
            <span>Has house photo</span>
          </label>
          <label
            className="checkbox-label"
            title="Only listings whose gallery includes a photo of water (pond, creek, lake, etc.)"
          >
            <input
              type="checkbox"
              checked={!!filters.hasWaterPhotoOnly}
              onChange={(e) => handleChange('hasWaterPhotoOnly', e.target.checked)}
            />
            <span>Has water photo</span>
          </label>
          <label
            className="checkbox-label"
            title="Only listings whose gallery includes a long-view photo (distant horizon / mountain / valley)"
          >
            <input
              type="checkbox"
              checked={!!filters.hasLongViewPhotoOnly}
              onChange={(e) => handleChange('hasLongViewPhotoOnly', e.target.checked)}
            />
            <span>Has long-view photo</span>
          </label>
        </div>
        <p style={{ fontSize: '0.7rem', color: '#999', margin: '4px 0 0 0' }}>
          Derived from per-photo Claude-vision tags. Listings not yet
          classified (or whose gallery has none of the requested feature)
          are excluded when any of these toggles is on.
        </p>
      </div>
      </>
      )}

      {showGroup('price-market') && (
      <>
      {/* Listing Price Filter */}
      <div className="filter-group">
        <label>Listing Price ($)</label>
        <div className="range-inputs">
          <input type="number" placeholder="Min" value={filters.minPrice || ''} onChange={(e) => handleChange('minPrice', e.target.value)} />
          <input type="number" placeholder="Max" value={filters.maxPrice || ''} onChange={(e) => handleChange('maxPrice', e.target.value)} />
        </div>
      </div>

      {/* $/Acre Filter (#112) — derived listing_price / area_acres.
          Backend excludes parcels with NULL/0 acres or no list price. */}
      <div className="filter-group">
        <label>Price per Acre ($/ac)</label>
        <div className="range-inputs">
          <input
            type="number"
            placeholder="Min"
            value={filters.minPricePerAcre || ''}
            onChange={(e) => handleChange('minPricePerAcre', e.target.value)}
          />
          <input
            type="number"
            placeholder="Max"
            value={filters.maxPricePerAcre || ''}
            onChange={(e) => handleChange('maxPricePerAcre', e.target.value)}
          />
        </div>
      </div>

      {/* Price-changed-recently filter (#114).  Surfaces parcels
          whose listing_price moved within the lookback window —
          good for spotting motivated sellers / freshly-cut deals. */}
      <div className="filter-group">
        <label>Price Changed Recently</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <label className="checkbox-label" style={{ flex: 1 }}>
            <input
              type="checkbox"
              checked={!!filters.priceChangedRecently}
              onChange={(e) => handleChange('priceChangedRecently', e.target.checked)}
            />
            {' '}
            <span>Within</span>
          </label>
          <input
            type="number"
            min="1"
            max="365"
            placeholder="30"
            value={filters.priceChangeDays || ''}
            onChange={(e) => handleChange('priceChangeDays', e.target.value)}
            disabled={!filters.priceChangedRecently}
            style={{ width: 60 }}
          />
          <span style={{ fontSize: '0.85rem' }}>days</span>
        </div>
      </div>

      {/* Days on Market Filter */}
      <div className="filter-group">
        <label>
          Days on Market
          <span className="score-value">
            {filters.domFilter || 'Any'}
          </span>
        </label>
        <select value={filters.domFilter || ''} onChange={(e) => handleChange('domFilter', e.target.value)}>
          <option value="">Any</option>
          <option value="<1">Under 1 day</option>
          <option value="<3">Under 3 days</option>
          <option value="<5">Under 5 days</option>
          <option value="<7">Under 1 week</option>
          <option value="<30">Under 30 days</option>
          <option value="<90">Under 90 days</option>
          <option value="<180">Under 6 months</option>
          <option value="<365">Under 1 year</option>
          <option value=">365">Over 1 year</option>
          <option value=">730">Over 2 years</option>
          <option value=">1095">Over 3 years</option>
        </select>
      </div>
      </>
      )}

      {showGroup('advanced') && (
      <>
      {/* Mineral Deposit Proximity Filters */}
      <div className="filter-group">
        <label>
          Max Distance to Deposit
          <span className="score-value">
            {filters.maxMineDist ? (filters.maxMineDist < 1 ? `${Math.round(filters.maxMineDist * 1000)}m` : `${Math.round(filters.maxMineDist * 0.621371)} mi`) : 'Any'}
          </span>
        </label>
        <select value={filters.maxMineDist || ''} onChange={(e) => handleChange('maxMineDist', e.target.value)}>
          <option value="">Any distance</option>
          <option value="0.1">On property (within 100m)</option>
          <option value="0.5">Within 0.3 mi (~500m)</option>
          <option value="1">Within 0.6 mi (~1 km)</option>
          <option value="2">Within 1.2 mi (~2 km)</option>
          <option value="5">Within 3 mi (~5 km)</option>
          <option value="10">Within 6 mi (~10 km)</option>
          <option value="25">Within 15 mi (~25 km)</option>
          <option value="50">Within 30 mi (~50 km)</option>
        </select>
      </div>

      <div className="filter-group">
        <label>
          Min Deposits within 5 mi
          <span className="score-value">
            {filters.minMinesNearby ? `${filters.minMinesNearby}+` : 'Any'}
          </span>
        </label>
        <select value={filters.minMinesNearby || ''} onChange={(e) => handleChange('minMinesNearby', e.target.value)}>
          <option value="">Any</option>
          <option value="1">At least 1</option>
          <option value="5">At least 5</option>
          <option value="10">At least 10</option>
          <option value="25">At least 25</option>
          <option value="50">At least 50</option>
        </select>
      </div>

      {/* Alt-energy generation-site proximity (#997). Mirrors the mine
          proximity control above: a km cap that maps to the backend
          `max_dist_generation_km` param, plus an optional per-tech
          restriction (`generation_tech`). The tech select is only
          meaningful with a distance set (backend requires the distance),
          so it's disabled until a distance is chosen. */}
      <div className="filter-group">
        <label>
          Max Distance to Generation Site
          <span className="score-value">
            {filters.maxGenerationDist ? (filters.maxGenerationDist < 1 ? `${Math.round(filters.maxGenerationDist * 1000)}m` : `${Math.round(filters.maxGenerationDist * 0.621371)} mi`) : 'Any'}
          </span>
        </label>
        <select value={filters.maxGenerationDist || ''} onChange={(e) => handleChange('maxGenerationDist', e.target.value)}>
          <option value="">Any distance</option>
          <option value="0.5">Within 0.3 mi (~500m)</option>
          <option value="1">Within 0.6 mi (~1 km)</option>
          <option value="2">Within 1.2 mi (~2 km)</option>
          <option value="5">Within 3 mi (~5 km)</option>
          <option value="10">Within 6 mi (~10 km)</option>
          <option value="25">Within 15 mi (~25 km)</option>
          <option value="50">Within 30 mi (~50 km)</option>
        </select>
      </div>

      <div className="filter-group">
        <label>Generation Technology</label>
        <select
          value={filters.generationTech || ''}
          onChange={(e) => handleChange('generationTech', e.target.value)}
          disabled={!filters.maxGenerationDist}
          title={filters.maxGenerationDist ? 'Restrict the proximity filter to one technology' : 'Set a max distance first'}
        >
          <option value="">Any technology</option>
          <option value="wind">Wind</option>
          <option value="solar">Solar</option>
          <option value="geothermal">Geothermal</option>
        </select>
      </div>

      {/* HIFLD transmission-line proximity (#991). A km cap that maps to
          the backend `max_powerline_dist` param (converted to metres at
          the API boundary), plus voltage / class / type filters that
          require a matching transmission line within that radius (or an
          8 km default when no distance is set — so the voltage/class/type
          filters stand alone and are NOT disabled by an empty distance,
          unlike the generation-tech control above). */}
      <div className="filter-group">
        <label>
          Max Distance to Transmission Line
          <span className="score-value">
            {filters.maxPowerlineDist ? (filters.maxPowerlineDist < 1 ? `${Math.round(filters.maxPowerlineDist * 1000)}m` : `${Math.round(filters.maxPowerlineDist * 0.621371)} mi`) : 'Any'}
          </span>
        </label>
        <select value={filters.maxPowerlineDist || ''} onChange={(e) => handleChange('maxPowerlineDist', e.target.value)}>
          <option value="">Any distance</option>
          <option value="0.5">Within 0.3 mi (~500m)</option>
          <option value="1">Within 0.6 mi (~1 km)</option>
          <option value="2">Within 1.2 mi (~2 km)</option>
          <option value="5">Within 3 mi (~5 km)</option>
          <option value="10">Within 6 mi (~10 km)</option>
          <option value="25">Within 15 mi (~25 km)</option>
          <option value="50">Within 30 mi (~50 km)</option>
        </select>
      </div>

      <div className="filter-group">
        <label>Min Transmission Voltage (kV)</label>
        <select value={filters.minPowerlineVoltage || ''} onChange={(e) => handleChange('minPowerlineVoltage', e.target.value)}>
          <option value="">Any voltage</option>
          <option value="69">69 kV+</option>
          <option value="115">115 kV+</option>
          <option value="230">230 kV+</option>
          <option value="345">345 kV+</option>
          <option value="500">500 kV+</option>
          <option value="765">765 kV+</option>
        </select>
      </div>

      <div className="filter-group">
        <label>Transmission Voltage Class</label>
        <select value={filters.powerlineVoltClass || ''} onChange={(e) => handleChange('powerlineVoltClass', e.target.value)}>
          <option value="">Any class</option>
          <option value="UNDER 100">Under 100 kV</option>
          <option value="100-161">100-161 kV</option>
          <option value="220-287">220-287 kV</option>
          <option value="345">345 kV</option>
          <option value="500">500 kV</option>
          <option value="735 AND ABOVE">735 kV and above</option>
          <option value="DC">DC</option>
        </select>
      </div>

      <div className="filter-group">
        <label>Transmission Line Type</label>
        <select value={filters.powerlineLineType || ''} onChange={(e) => handleChange('powerlineLineType', e.target.value)}>
          <option value="">Any type</option>
          <option value="AC">AC</option>
          <option value="DC">DC</option>
        </select>
      </div>

      {/* Commodity Probability Filters */}
      <div className="filter-group">
        <label>Mineral Probability (min %)</label>
        <div className="commodity-filters">
          {[
            { key: 'minGoldProb', label: 'Gold', color: '#FFD700' },
            { key: 'minCopperProb', label: 'Copper', color: '#B87333' },
            { key: 'minSilverProb', label: 'Silver', color: '#C0C0C0' },
            { key: 'minUraniumProb', label: 'Uranium', color: '#7FFF00' },
            { key: 'minLithiumProb', label: 'Lithium', color: '#00CED1' },
            { key: 'minReeProb', label: 'REE', color: '#FF69B4' },
            // Niobium (#990) — modeled commodity that was missing from the
            // filter surface. Color matches the Nb tile in ParcelDetail.
            { key: 'minNiobiumProb', label: 'Niobium', color: '#9370DB' },
          ].map(({ key, label, color }) => (
            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              <span style={{
                display: 'inline-block', width: 10, height: 10, borderRadius: 2,
                backgroundColor: color, flexShrink: 0,
              }} />
              <span style={{ fontSize: '0.8rem', width: 60 }}>{label}</span>
              <select
                value={filters[key] || ''}
                onChange={(e) => handleChange(key, e.target.value)}
                style={{ flex: 1, fontSize: '0.8rem' }}
              >
                <option value="">Any</option>
                <option value="0.1">10%+</option>
                <option value="0.25">25%+</option>
                <option value="0.5">50%+</option>
                <option value="0.75">75%+</option>
                <option value="0.9">90%+</option>
              </select>
            </div>
          ))}
        </div>
      </div>
      </>
      )}

      {showGroup('land-quality') && (
      <>
      {/* Hazard Risk Filters (FEMA NRI) */}
      <div className="filter-group">
        <label>Hazard Risk (FEMA NRI)</label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 60, fontSize: '0.8rem', color: '#e94560' }}>Wildfire</span>
            <select value={filters.maxWildfireRisk || ''} onChange={(e) => handleChange('maxWildfireRisk', e.target.value)}
              style={{ flex: 1, padding: '4px 6px', borderRadius: 4, border: '1px solid #ddd', fontSize: '0.82rem' }}>
              <option value="">Any</option>
              <option value="20">Low (&lt;20)</option>
              <option value="40">Below Avg (&lt;40)</option>
              <option value="60">Moderate (&lt;60)</option>
              <option value="80">Above Avg (&lt;80)</option>
              <option value="101">Any (show all)</option>
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 60, fontSize: '0.8rem', color: '#2171b5' }}>Flood</span>
            <select value={filters.maxFloodRisk || ''} onChange={(e) => handleChange('maxFloodRisk', e.target.value)}
              style={{ flex: 1, padding: '4px 6px', borderRadius: 4, border: '1px solid #ddd', fontSize: '0.82rem' }}>
              <option value="">Any</option>
              <option value="20">Low (&lt;20)</option>
              <option value="40">Below Avg (&lt;40)</option>
              <option value="60">Moderate (&lt;60)</option>
              <option value="80">Above Avg (&lt;80)</option>
              <option value="101">Any (show all)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Elevation change (#365) — same filter shipped on /properties.
          min/max are meters of elevation range across the parcel
          (max - min from the DEM-derived geo_features.elevation_range).
          Backend short-circuits with 503 when the column is still
          unpopulated; once the backfill (issue #399) runs this
          becomes a useful proxy for "flat vs steep". */}
      <div className="filter-group">
        <label>Elevation Change (m)</label>
        <div style={{ display: 'flex', gap: 6 }}>
          <input
            type="number" min="0" placeholder="min"
            value={filters.minElevationChange || ''}
            onChange={(e) => handleChange('minElevationChange', e.target.value)}
            aria-label="Min elevation change meters"
            style={{ flex: 1, padding: '4px 6px', borderRadius: 4, border: '1px solid #ddd', fontSize: '0.82rem' }}
          />
          <input
            type="number" min="0" placeholder="max"
            value={filters.maxElevationChange || ''}
            onChange={(e) => handleChange('maxElevationChange', e.target.value)}
            aria-label="Max elevation change meters"
            style={{ flex: 1, padding: '4px 6px', borderRadius: 4, border: '1px solid #ddd', fontSize: '0.82rem' }}
          />
        </div>
      </div>
      </>
      )}

      {!group && actions}
    </div>
  );
}

export default FilterPanel;
